import { FC } from "react";
import Button from "../ui/Button";
import { Modal } from "../ui/Modal";
import { IEntityChangeGroup } from "../../interfaces/endpoints/IEntityChangeLog.types";
import ChangeLine from "./ChangeLine";
import RevertActionButton from "./RevertActionButton";
import { describeWho } from "./changeHistoryGroups";
import { actionLabel, actionStyle, describeRevertOf, formatUtc, labelForEntity } from "./changeHistoryLabels";
import { changeLines } from "./changeSummary";
import { actionBlockedSentence } from "./revertText";

interface IActionDetailsModal {
  group: IEntityChangeGroup;
  onClose: () => void;
  // Opens one record of the action in full, with its previous state and its own revert.
  onOpenRow: (logId: number) => void;
  onReverted: () => void;
}

const named = (entityName: string, display: string | null, id: number) =>
  `${labelForEntity(entityName)} ${display ?? `#${id}`}`;

// Everything one action wrote, in one list: an admin who edits a trip changes its stop times and prices
// too, and this is where those records are read one by one, or undone together.
const ActionDetailsModal: FC<IActionDetailsModal> = ({ group, onClose, onOpenRow, onReverted }) => (
  <Modal
    show
    position="center"
    title={`${named(group.entityName, group.entityDisplay, group.entityId)}: ${group.rowCount} ndryshime`}
    onClose={onClose}
    modalWidth="min(64rem, 95vw)"
    fitContent
  >
    <div className="p-5 text-sm">
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 mb-5">
        <dt className="text-gray-500">Kush</dt>
        <dd>{describeWho(group.actorName, group.actorType, group.actorId)}</dd>
        <dt className="text-gray-500">Kur</dt>
        <dd>{formatUtc(group.changedAtUtc)}</dd>
      </dl>

      {group.rowCount > group.rows.length && (
        <p className="text-xs text-gray-500 mb-2">
          Po shfaqen {group.rows.length} rreshtat e parë nga {group.rowCount}.
        </p>
      )}
      <div className="overflow-x-auto mb-5">
        <table className="w-full text-left border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 font-medium">Çfarë</th>
              <th className="px-3 py-2 font-medium">Veprimi</th>
              <th className="px-3 py-2 font-medium">Ndryshimet</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {group.rows.map((row) => (
              <tr key={row.id} className="border-t border-gray-100 align-top">
                <td className="px-3 py-2">
                  <span className="block">{named(row.entityName, row.entityDisplay, row.entityId)}</span>
                  {row.parentEntityName && (
                    <span className="block text-xs text-gray-500">
                      nën {named(row.parentEntityName, row.parentEntityDisplay, row.parentEntityId ?? 0)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${actionStyle(row)}`}>
                    {actionLabel(row)}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {describeRevertOf(row) && <span className="block">{describeRevertOf(row)}</span>}
                  {changeLines(row, Infinity).map((line) => (
                    <ChangeLine key={line} line={line} />
                  ))}
                  {!describeRevertOf(row) && changeLines(row, Infinity).length === 0 && "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button
                    btnType="custom"
                    padding="px-2 py-1"
                    className="text-primary"
                    onClick={() => onOpenRow(row.id)}
                  >
                    Detajet
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-gray-200 pt-4">
        <p className="text-gray-500">
          {group.canRevert
            ? `Kthimi i gjithë veprimit i kthen të ${group.rowCount} ndryshimet siç ishin para tij.`
            : actionBlockedSentence(group.revertBlockedCode, group.revertBlockedReason)}
        </p>
        {group.canRevert && <RevertActionButton group={group} onReverted={onReverted} />}
      </div>
    </div>
  </Modal>
);

export default ActionDetailsModal;
